import * as React from "react";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { NotificationSettings } from "./NotificationSettings/NotificationSettings";
import { ProfileCreateDialog } from "./ProfileCreateDialog";
import {
  DemoModeSwitcher,
  type DemoMode,
} from "./DemoModeSwitcher";
import type { ProfileMeta } from "../types";
import type { useProfileTitle } from "../hooks/useProfileTitle";

const PROFILE_TITLE_MAX_LEN = 64;

type TitleState = ReturnType<typeof useProfileTitle>;

export type ProfileWorkspaceHeaderProps = {
  selectedProfileTitle: string;
  selectedMeta: ProfileMeta | undefined;
  canRenameProfile: boolean;
  canDeleteProfile: boolean;
  canAddViaModal: boolean;
  title: TitleState;
  demoMode: DemoMode;
  onDemoModeChange: (mode: DemoMode) => void;
  onRequestDelete: () => void;
  onSubmitQuestionnaire: (telephone: string) => void;
  registerTourTarget?: (id: string, el: HTMLElement | null) => void;
  /** When true, keep add-questionnaire enabled even during processing (tour hold). */
  tourKeepAddEnabled?: boolean;
};

export function ProfileWorkspaceHeader({
  selectedProfileTitle,
  selectedMeta,
  canRenameProfile,
  canDeleteProfile,
  canAddViaModal,
  title,
  demoMode,
  onDemoModeChange,
  onRequestDelete,
  onSubmitQuestionnaire,
  registerTourTarget,
  tourKeepAddEnabled = false,
}: ProfileWorkspaceHeaderProps) {
  const {
    isEditingProfileTitle,
    draftProfileTitle,
    isRenamingProfile,
    profileRenameError,
    titleInputRef,
    setDraftProfileTitle,
    beginEditProfileTitle,
    cancelEditProfileTitle,
    commitProfileTitle,
  } = title;
  const [addQuestionnaireRequested, setIsAddQuestionnaireOpen] = React.useState(false);
  const isAddQuestionnaireOpen = addQuestionnaireRequested && canAddViaModal;

  const renameRef = React.useCallback(
    (el: HTMLButtonElement | null) => {
      registerTourTarget?.("rename-profile", el);
    },
    [registerTourTarget],
  );
  const deleteRef = React.useCallback(
    (el: HTMLButtonElement | null) => {
      registerTourTarget?.("delete-profile", el);
    },
    [registerTourTarget],
  );
  const addQuestionnaireRef = React.useCallback(
    (el: HTMLElement | null) => {
      registerTourTarget?.("add-questionnaire", el);
    },
    [registerTourTarget],
  );

  const addDisabled =
    selectedMeta?.status === "processing" && !tourKeepAddEnabled;

  return (
    <>
    <div className="sticky top-0 z-20 pt-2 pb-4">
      <div className="absolute w-full h-28 top-0 bg-linear-to-b from-neutral-background-200 via-neutral-background-200 to-transparent z-[-1] pointer-events-none" />
      <div className="relative z-10 flex min-h-11 w-full items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex w-full items-center gap-2">
            {isEditingProfileTitle ? (
              <div className="flex max-w-full min-w-0 items-center gap-2">
                <div className="flex h-14 min-w-0 max-w-full items-center rounded-m border border-neutral-border-300 pl-4 pr-2">
                  <div className="grid min-w-12 overflow-hidden">
                    <span className="col-start-1 row-start-1 invisible overflow-hidden text-clip whitespace-pre px-0 text-h1 font-medium">
                      {draftProfileTitle || " "}
                    </span>
                    <Input
                      ref={titleInputRef}
                      value={draftProfileTitle}
                      onChange={(e) => setDraftProfileTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void commitProfileTitle();
                        if (e.key === "Escape") cancelEditProfileTitle();
                      }}
                      maxLength={PROFILE_TITLE_MAX_LEN}
                      aria-label="Название профиля"
                      className="col-start-1 row-start-1 h-12 w-full truncate bg-transparent! px-0 text-h1 font-medium hover:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                      disabled={isRenamingProfile}
                    />
                  </div>
                  <div className="ml-3 flex shrink-0 items-center gap-1 border-l border-neutral-border-300 pl-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => void commitProfileTitle()}
                      loading={isRenamingProfile}
                      aria-label="Сохранить название"
                    >
                      {!isRenamingProfile ? <Check className="h-4 w-4" /> : null}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      onClick={cancelEditProfileTitle}
                      disabled={isRenamingProfile}
                      aria-label="Отменить"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="truncate text-h1 font-medium">
                  {selectedProfileTitle}
                </div>
                {canRenameProfile && (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      ref={renameRef}
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      data-tour-target="rename-profile"
                      onClick={beginEditProfileTitle}
                      aria-label="Редактировать название профиля"
                    >
                      <Pencil
                        strokeWidth={2.5}
                        className="h-4 w-4 text-neutral-text-500"
                      />
                    </Button>
                    {canDeleteProfile && (
                      <Button
                        ref={deleteRef}
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full text-neutral-text-500 hover:text-state-negative"
                        data-tour-target="delete-profile"
                        onClick={onRequestDelete}
                        aria-label="Удалить профиль"
                      >
                        <Trash2
                          strokeWidth={2.5}
                          className="h-4 w-4 transition-colors"
                        />
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          {isEditingProfileTitle && profileRenameError && (
            <div className="mt-2 text-caption text-destructive">
              {profileRenameError}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <DemoModeSwitcher
            value={demoMode}
            onValueChange={onDemoModeChange}
            variant="header"
          />
          <NotificationSettings registerTourTarget={registerTourTarget} />
          {canAddViaModal ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  ref={addQuestionnaireRef}
                  className="inline-flex"
                  data-tour-target="add-questionnaire"
                >
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => setIsAddQuestionnaireOpen(true)}
                    aria-label="Добавить анкету"
                    disabled={addDisabled}
                  >
                    <Plus strokeWidth={2.5} className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Добавить анкету</TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      </div>
    </div>
    {canAddViaModal ? (
      <ProfileCreateDialog
        open={isAddQuestionnaireOpen}
        onOpenChange={setIsAddQuestionnaireOpen}
        disabled={
          selectedMeta?.status === "processing" ||
          Boolean(selectedMeta?.isLoadingProfileData)
        }
        onSubmit={(telephone: string) => {
          setIsAddQuestionnaireOpen(false);
          onSubmitQuestionnaire(telephone);
        }}
      />
    ) : null}
    </>
  );
}
